// File to add constants to be used throughout the app

import { ICampaignsPageLayout } from '../models/core-models';

export class Constants {}

export const MAIN_WEBSITE_URL = 'https://mylead.global/'; // global
export const STATIC_URL = 'https://static.mylead.eu/'; // eu
export const STATIC_API_URL = 'https://api.mylead.global/api/external/v1/static'; // global
export const CONTACT_MAILTO_URL = 'mailto:contact@mylead.eu'; // eu
export const CONTACT_MAILTO_TEXT = 'contact@mylead.eu'; // eu
export const BACKUP_LANGUAGES: string =
  '{"en":{"name":"English","script":"Latn","native":"English"},"es":{"name":"Spanish","script":"Latn","native":"español"},"pl":{"name":"Polish","script":"Latn","native":"polski"},"ru":{"name":"Russian","script":"Cyrl","native":"русский"},"uk":{"name":"Ukrainian","script":"Cyrl","native":"українська"}}';

export const GLOBAL_RESIZE_DEBOUNCE_TIME = 200;
export const GLOBAL_INPUT_DEBOUNCE_TIME = 200;
export const GLOBAL_SCROLL_DEBOUNCE_TIME = 50;

export const DEFAULT_NOTIFICATIONS_CATEGORY = 'active_notifications';

// miliseconds per 24 hours
export const _MS_PER_DAY = 1000 * 60 * 60 * 24;

// list of page layouts and corresponding icons
const pageLayouts: ICampaignsPageLayout[] = [
  { name: 'tiles', icon: 'more' },
  { name: 'tiles-alt', icon: 'layout-columns' },
  { name: 'list', icon: 'layout-rows' }
];

export const PageLayouts = pageLayouts;

// default page layout is first in PageLayouts table
export const defaultPageLayout = PageLayouts[0];

// list of notification types
const notificationTypes = ['success', 'danger', 'warning', 'info'] as const;

/**
 * notification types: 0 - success, 1 - danger, 2 - warning, 3 - info
 */
export const NotificationTypes: typeof notificationTypes = notificationTypes;

// list of alternative notification types, used for socked notification
const alternativeNotificationTypes = ['success', 'info', 'warning', 'success', 'danger', 'info', 'info'] as const;

/**
 * notification types: 0 - success, 1 - info, 2 - warning, 3 - success, 4 - danger, 5 & 6 - info
 */
export const AlternativeNotificationTypes: typeof alternativeNotificationTypes = alternativeNotificationTypes;

/**
 * url of default user image that is used if current image has error
 */
export const defaultUserImage = './assets/images/deafult-user-image.png';

/**
 * url of default user icon that is used if current avatar
 */
export const defaultChatIcon = './assets/images/logo-ml-chat-black.png';

/**
 * url of default image that is used if current image has error
 */
// export const defaultImage = './assets/images/default-image.png';
export const defaultImage = './assets/images/default-campaign-image-2.png';
export const defaultImageWhite = './assets/images/default-image-white.png';

/**
 * campaign filter categories with their params to send to backend
 */
export const campaignFiltersMap = {
  'type[]': 'type',
  live: 'live',
  recommended: 'recommended',
  beginner: 'beginner',
  is_new: 'is_new',
  status: 'status',
  'device_type[]': 'device_types',
  'category[]': 'categories',
  'incent_type[]': 'incent_types',
  'conversion_type[]': 'conversion_types',
  'country[]': 'countries'
};

export const CAMPAIGN_IGNORED_FILTERS = ['per_page', 'order_field', 'order_type'];

/**
 * Filter elements that have different name than translations. Used for right translation of filter groups (group names like
 * countries, device types etc.)
 */
export const campaignFiltersCategoriesElements = {
  recommendedFilterLabel: 'recommended',
  liveFilterLabel: 'live',
  beginnerFilterLabel: 'beginner',
  isNewFilterLabel: 'is_new',
  campaign_type: 'types',
  additional_functionalities: 'additional',
  status: 'statuses',
  device_type: 'device_types',
  search: 'search'
};

/**
 * Filter elements that have different name than translations. Used for right translation of filter elements (like mobile, CPL,
 * Poland etc.)
 */
export const campaignFiltersCategories = {
  recommendedFilterLabel: 'recommended',
  liveFilterLabel: 'live',
  beginnerFilterLabel: 'beginner',
  isNewFilterLabel: 'is_new',
  additional_functionalities: 'additional',
  statuses: 'status',
  types: 'type'
};

const languages = [
  {
    id: 0,
    language: 'English (EN)',
    country: 'united-kingdom',
    short: 'en',
    flag: 'gb'
  },
  {
    id: 1,
    language: 'Español (ES)',
    country: 'spain',
    short: 'es',
    flag: 'es'
  },
  {
    id: 2,
    language: 'Pусский (RU)',
    country: 'russia',
    short: 'ru',
    flag: 'ru'
  },
  {
    id: 3,
    language: 'Yкраїнська (UA)',
    country: 'ukraine',
    short: 'ua',
    flag: 'ua'
  }
] as const;

export const AppDefaultLanguage = languages[0];

export const AppLanguages = languages;

/**
 * @description Interface for validation expressions
 */
interface ValidationConst {
  readonly inputWithoutSpecialCharacters: RegExp;
  readonly emailFormat: RegExp;
  readonly emailSpecialCharacters: RegExp;
  readonly nipNumber: RegExp;
  readonly url: RegExp;
  readonly namePattern: RegExp;
  readonly loginPattern: RegExp;
  readonly phoneNumberPattern: RegExp;
  readonly agePattern: RegExp;
  readonly number: RegExp;
  readonly colorPattern: RegExp;
  readonly fileSizePattern: RegExp;
  readonly timePattern: RegExp;
  readonly password: RegExp;
  readonly currencyMultiplier: RegExp;
}

/**
 * @description Regex validation expressions
 */

const ValidationExpressions: ValidationConst = {
  inputWithoutSpecialCharacters: /^[^~!@#$%&*\[\]\{\}\<\>\^+=:,;?/\\]+$/,
  emailFormat:
    /^([-!#-'*+/-9=?A-Z^-~]+(\.[-!#-'*+/-9=?A-Z^-~]+)*|"([]!#-[^-~ \t]|(\\[\t -~]))+")@[0-9A-Za-z]([0-9A-Za-z-]{0,61}[0-9A-Za-z])?(\.[0-9A-Za-z]([0-9A-Za-z-]{0,61}[0-9A-Za-z])?)+/,
  emailSpecialCharacters: /^[^~!#$%&*\[\]\{\}\<\>\^+=:,;?/\\]+$/,
  nipNumber: /^[0-9]{3}-[0-9]{3}-[0-9]{2}-[0-9]{2}$/,
  // url: /^(?:http(s)?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/g,
  url: /^(?:http(s)?:\/\/)?(www\.)?(?:[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-z]{2,6}|(?:\d{1,3}\.){3}\d{1,3})\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/g,
  namePattern: /[a-zA-Z\xC0-\uFFFF-]/g,
  loginPattern: /^(?=[a-zA-Z0-9@._-]{1,24}$)(?!.*[_.]{2})[^_.].*[^_.]$/,
  phoneNumberPattern: /^(?:\+\d{1,3})?\s*(?:\d{2,3}\s*-?\s*){2,3}\d{3,4}$/,
  agePattern: /[0-9]{1}[0-9]{0,1}/,
  number: /^[0-9]*$/,
  colorPattern: /^#([0-9a-f]{6}|[0-9a-f]{3})$/,
  fileSizePattern: /^[. 0-9]+(KB|MB|GB|TB)$/,
  timePattern: /^(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d$/,
  // password: /(?=^.{8,}$)(?=.*\d)(?=.*[!@#$%^&*]+)(?![.\n])(?=.*[A-Z])(?=.*[a-z]).*$/,
  password: /(?=.{8,})(?=.*[a-z])(?=.*[A-Z])(?=.*[a-zA-Z])(?=.*[!@#$%^&*+=?_,.;:-])(?=.*\d).*/,
  currencyMultiplier: /^(?!-)(?!0(?:\.0{1,2})?$)(?:\d{0,7}(?:,\d{3})*(?:\.\d{1,2})?|10000000(?:\.0{1,2})?|[1-9]\d{0,6}(?:,\d{3})*(?:\.\d{1,2})?)$/

  // /^(?!-)(?!0(?:\.0{1,2})?$)(?:\d{0,7}(?:,\d{3})*(?:\.\d{1,2})?|10000000(?:\.0{1,2})?|[1-9]\d{0,6}(?:,\d{3})*(?:\.\d{1,2})?)$/ 10kk
  // currencyMultiplier: /^(?!-)(?!0(?:\.0{1,2})?$)(?:\d{0,3}(?:,\d{3})*(?:\.\d{1,2})?|1000(?:\.0{1,2})?|5000(?:\.0{1,2})?|[1-4]\d{0,3}(?:,\d{3})*(?:\.\d{1,2})?)$/  5k
  // currencyMultiplier: /^(?!0(?:\.0{1,2})?$)(?:\d{0,3}(?:,\d{3})*(?:\.\d{1,2})?|1000(?:\.0{1,2})?)$/  1k
  // const numberRegex = /^(?!-)(?!0(?:\.0{1,2})?$)(?:\d{0,4}(?:,\d{3})*(?:\.\d{1,2})?|10000(?:\.0{1,2})?|[1-9]\d{0,3}(?:,\d{3})*(?:\.\d{1,2})?)$/; 10k
};

export const validationExpressions: ValidationConst = ValidationExpressions;

// Validations

export const regexList = [
  {
    id: 0,
    name: 'bankAccount',
    regex: /^([A-Z]{2}[ '+'\\'+'-]?[0-9]{2})(?=(?:[ '+'\\'+'-]?[A-Z0-9]){9,30}$)((?:[ '+'\\'+'-]?[A-Z0-9]{3,5}){2,7})([ '+'\\'+'-]?[A-Z0-9]{1,3})?$/
  },
  { id: 1, name: 'email', regex: /^[^\s@]+@[^\s@]+\.[^\s@]+$/ },
  { id: 2, name: 'skrill', regex: /^.{5,}$/ },
  { id: 3, name: 'tether', regex: /^.{5,}$/ },
  {
    id: 4,
    name: 'revolut',
    regex: /^([A-Z]{2}[ '+'\\'+'-]?[0-9]{2})(?=(?:[ '+'\\'+'-]?[A-Z0-9]){9,30}$)((?:[ '+'\\'+'-]?[A-Z0-9]{3,5}){2,7})([ '+'\\'+'-]?[A-Z0-9]{1,3})?$/
  },
  { id: 5, name: 'bitcoin', regex: /^[13][a-km-zA-HJ-NP-Z1-9]{25,34}$/ },
  { id: 6, name: 'url', regex: /(http|ftp|https):\/\/[\w-]+(\.[\w-]+)+([\w.,@?^=%&amp;:\/~+#-]*[\w@?^=%&amp;\/~+#-])?/ },
  { id: 7, name: 'uuid', regex: /^\d+|[a-fA-F\d]{8}-[a-fA-F\d]{4}-[a-fA-F\d]{4}-[a-fA-F\d]{4}-[a-fA-F\d]{12}$|^[\da-fA-F]{32}$/ },
  // { id: 8, name: 'usdc', regex: /^(0x[a-fA-F0-9]{40}|T[a-zA-Z0-9]{33}|[1-9A-HJ-NP-Za-km-z]{32,44})$/ }
  { id: 8, name: 'usdc', regex: /^.{5,}$/ }
];

export const paymentsMethodValidation = method => {
  const regex = regexList[method.id].regex;
  const result = regex.test(method.inputValue);
  return result;
};

/**
 * Map that holds additional features available filters for campaign filters
 */
export const additionalFeaturesMap = {
  1: 'available_deeplink',
  2: 'available_banners',
  3: 'is_smartlink',
  4: 'available_products',
  5: 'is_comparison_campaign',
  6: 'available_coupons',
  7: 'available_hidelink'
};

/**
 * MyLead Bot username
 */
export const botUsername = 'MyLead Bot';

export const leadsStatuses = {
  '-1': 'any',
  0: 'available-to-pay',
  1: 'pending',
  2: 'rejected',
  3: 'accepted'
};

export const leadsStatusesTranslated = {
  '-1': 'all',
  0: 'approved',
  1: 'pending',
  2: 'rejected',
  3: 'pre_approved'
};

/**
 * Statuses of promotion in IVR
 */
export const promotionStatuses = {
  unreported: null,
  waiting: 0,
  active: 1,
  rejected: 2
};

export const forumNewsCategoryId = {
  pl: 1,
  en: 11,
  es: 11,
  ru: 21,
  uk: 31
};

/**
 * All possible values for dropdown
 * 0, 1 date_created
 * 2, 3 - amount
 * 4, 5 - date_modified
 * 6, 7 - wallet
 * 8, 9 - date_activated
 * 10, 11 - name
 * 12, 13 - program_id
 * 14, 15 - config_id
 * 16, 17 - price
 * 18, 19 - status
 * 20, 21 - rate
 * 22, 23 - amount with rate name
 * 24, 25, 26, 27, 28 - layout grid (8, 25, 50, 100, 200)
 * 29, 30 - end_date
 * 31, 32 - earn
 * 33, 34 - earn_payment_waiting
 * 35, 36 - earn_wait
 * 37, 38 - earn_rejected
 * 39, 40 - lead
 * 41, 42 - visits
 * 43, 44 - conversion
 * 45, 46 - acceptance
 * 47, 48 - epc
 * 49, 50 - id with program id name
 * 51, 52 - earnings
 * 53, 54 - leads
 * 55, 56 - day
 * 57, 58 - leadID with id parameter name
 * 59, 60 - visitID with id parameter name
 * 61, 62 - country
 * 63, 64 - username
 * 65, 66 - date_created ascending => oldest first, desc => newest first
 * 83, 84 - admin_program_id
 */
const ValuesForDropdown = [
  {
    name: 'newest',
    orderField: 'date_created',
    orderType: 'asc',
    id: 0
  },
  {
    name: 'oldest',
    orderField: 'date_created',
    orderType: 'desc',
    id: 1
  },
  {
    name: 'amount',
    additional: 'ascending',
    orderField: 'amount',
    orderType: 'asc',
    id: 2
  },
  {
    name: 'amount',
    additional: 'descending',
    orderField: 'amount',
    orderType: 'desc',
    id: 3
  },
  {
    name: 'updated_at',
    additional: 'ascending',
    orderField: 'date_modified',
    orderType: 'asc',
    id: 4
  },
  {
    name: 'updated_at',
    additional: 'descending',
    orderField: 'date_modified',
    orderType: 'desc',
    id: 5
  },
  {
    name: 'wallet_name',
    additional: 'ascending',
    orderField: 'wallet',
    orderType: 'asc',
    id: 6
  },
  {
    name: 'wallet_name',
    additional: 'descending',
    orderField: 'wallet',
    orderType: 'desc',
    id: 7
  },
  {
    name: 'activated_at',
    additional: 'ascending',
    orderField: 'date_activated',
    orderType: 'asc',
    id: 8
  },
  {
    name: 'activated_at',
    additional: 'descending',
    orderField: 'date_activated',
    orderType: 'desc',
    id: 9
  },
  {
    name: 'by_name',
    additional: 'ascending',
    orderField: 'name',
    orderType: 'asc',
    id: 10
  },
  {
    name: 'by_name',
    additional: 'descending',
    orderField: 'name',
    orderType: 'desc',
    id: 11
  },
  {
    name: 'by_program_id',
    additional: 'ascending',
    orderField: 'program_id',
    orderType: 'asc',
    id: 12
  },
  {
    name: 'by_program_id',
    additional: 'descending',
    orderField: 'program_id',
    orderType: 'desc',
    id: 13
  },
  {
    name: 'by_config_id',
    additional: 'ascending',
    orderField: 'config_id',
    orderType: 'asc',
    id: 14
  },
  {
    name: 'by_config_id',
    additional: 'descending',
    orderField: 'config_id',
    orderType: 'desc',
    id: 15
  },
  {
    name: 'price',
    additional: 'ascending',
    orderField: 'price',
    orderType: 'asc',
    id: 16
  },
  {
    name: 'price',
    additional: 'descending',
    orderField: 'price',
    orderType: 'desc',
    id: 17
  },
  {
    name: 'status',
    additional: 'ascending',
    orderField: 'status',
    orderType: 'asc',
    id: 18
  },
  {
    name: 'status',
    additional: 'descending',
    orderField: 'status',
    orderType: 'desc',
    id: 19
  },
  {
    name: 'rate',
    additional: 'ascending',
    orderField: 'rate',
    orderType: 'asc',
    id: 20
  },
  {
    name: 'rate',
    additional: 'descending',
    orderField: 'rate',
    orderType: 'desc',
    id: 21
  },
  {
    name: 'rate',
    additional: 'ascending',
    orderField: 'amount',
    orderType: 'asc',
    id: 22
  },
  {
    name: 'rate',
    additional: 'descending',
    orderField: 'amount',
    orderType: 'desc',
    id: 23
  },
  {
    name: '8',
    orderField: '8',
    orderType: '8',
    icon: 'layout-grid',
    id: 24
  },
  {
    name: '25',
    orderField: '25',
    orderType: '25',
    icon: 'layout-grid',
    id: 25
  },
  {
    name: '50',
    orderField: '50',
    orderType: '50',
    icon: 'layout-grid',
    id: 26
  },
  {
    name: '100',
    orderField: '100',
    orderType: '100',
    icon: 'layout-grid',
    id: 27
  },
  {
    name: '200',
    orderField: '200',
    orderType: '200',
    icon: 'layout-grid',
    id: 28
  },
  {
    name: 'end_date',
    additional: 'descending',
    orderField: 'end_date',
    orderType: 'desc',
    id: 29
  },
  {
    name: 'end_date',
    additional: 'ascending',
    orderField: 'end_date',
    orderType: 'asc',
    id: 30
  },
  {
    name: 'earns',
    additional: 'descending',
    orderField: 'earn',
    orderType: 'desc',
    id: 31
  },
  {
    name: 'earns',
    additional: 'ascending',
    orderField: 'earn',
    orderType: 'asc',
    id: 32
  },
  {
    name: 'accepted',
    additional: 'descending',
    orderField: 'earn_payment_waiting',
    orderType: 'desc',
    id: 33
  },
  {
    name: 'accepted',
    additional: 'ascending',
    orderField: 'earn_payment_waiting',
    orderType: 'asc',
    id: 34
  },
  {
    name: 'pending',
    additional: 'descending',
    orderField: 'earn_wait',
    orderType: 'desc',
    id: 35
  },
  {
    name: 'pending',
    additional: 'ascending',
    orderField: 'earn_wait',
    orderType: 'asc',
    id: 36
  },
  {
    name: 'rejected',
    additional: 'descending',
    orderField: 'earn_rejected',
    orderType: 'desc',
    id: 37
  },
  {
    name: 'rejected',
    additional: 'ascending',
    orderField: 'earn_rejected',
    orderType: 'asc',
    id: 38
  },
  {
    name: 'leads',
    additional: 'descending',
    orderField: 'lead',
    orderType: 'desc',
    id: 39
  },
  {
    name: 'leads',
    additional: 'ascending',
    orderField: 'lead',
    orderType: 'asc',
    id: 40
  },
  {
    name: 'visits',
    additional: 'descending',
    orderField: 'visits',
    orderType: 'desc',
    id: 41
  },
  {
    name: 'visits',
    additional: 'ascending',
    orderField: 'visits',
    orderType: 'asc',
    id: 42
  },
  {
    name: 'conversion',
    additional: 'descending',
    orderField: 'conversion',
    orderType: 'desc',
    id: 43
  },
  {
    name: 'conversion',
    additional: 'ascending',
    orderField: 'conversion',
    orderType: 'asc',
    id: 44
  },
  {
    name: 'acceptance_factor',
    additional: 'descending',
    orderField: 'acceptance',
    orderType: 'desc',
    id: 45
  },
  {
    name: 'acceptance_factor',
    additional: 'ascending',
    orderField: 'acceptance',
    orderType: 'asc',
    id: 46
  },
  {
    name: 'epc',
    additional: 'descending',
    orderField: 'epc',
    orderType: 'desc',
    id: 47
  },
  {
    name: 'epc',
    additional: 'ascending',
    orderField: 'epc',
    orderType: 'asc',
    id: 48
  },
  {
    name: 'by_program_id',
    additional: 'ascending',
    orderField: 'id',
    orderType: 'asc',
    id: 49
  },
  {
    name: 'by_program_id',
    additional: 'descending',
    orderField: 'id',
    orderType: 'desc',
    id: 50
  },
  {
    name: 'earns',
    additional: 'descending',
    orderField: 'earnings',
    orderType: 'desc',
    id: 51
  },
  {
    name: 'earns',
    additional: 'ascending',
    orderField: 'earnings',
    orderType: 'asc',
    id: 52
  },
  {
    name: 'leads',
    additional: 'descending',
    orderField: 'leads',
    orderType: 'desc',
    id: 53
  },
  {
    name: 'leads',
    additional: 'ascending',
    orderField: 'leads',
    orderType: 'asc',
    id: 54
  },
  {
    name: 'one_day',
    additional: 'descending',
    orderField: 'day',
    orderType: 'desc',
    id: 55
  },
  {
    name: 'one_day',
    additional: 'ascending',
    orderField: 'day',
    orderType: 'asc',
    id: 56
  },
  {
    name: 'lead_id',
    additional: 'descending',
    orderField: 'id',
    orderType: 'desc',
    id: 57
  },
  {
    name: 'lead_id',
    additional: 'ascending',
    orderField: 'id',
    orderType: 'asc',
    id: 58
  },
  {
    name: 'visit_id',
    additional: 'descending',
    orderField: 'id',
    orderType: 'desc',
    id: 59
  },
  {
    name: 'visit_id',
    additional: 'ascending',
    orderField: 'id',
    orderType: 'asc',
    id: 60
  },
  {
    name: 'countries',
    additional: 'descending',
    orderField: 'country',
    orderType: 'desc',
    id: 61
  },
  {
    name: 'countries',
    additional: 'ascending',
    orderField: 'country',
    orderType: 'asc',
    id: 62
  },
  {
    name: 'username',
    additional: 'descending',
    orderField: 'username',
    orderType: 'desc',
    id: 63
  },
  {
    name: 'username',
    additional: 'ascending',
    orderField: 'username',
    orderType: 'asc',
    id: 64
  },
  {
    name: 'oldest',
    orderField: 'date_created',
    orderType: 'asc',
    id: 65
  },
  {
    name: 'newest',
    orderField: 'date_created',
    orderType: 'desc',
    id: 66
  },
  {
    name: 'code_activation_date',
    additional: 'descending',
    orderField: 'created_at',
    orderType: 'desc',
    id: 67
  },
  {
    name: 'code_activation_date',
    additional: 'ascending',
    orderField: 'created_at',
    orderType: 'asc',
    id: 68
  },
  {
    name: 'expiration_date',
    additional: 'descending',
    orderField: 'expiration_date',
    orderType: 'desc',
    id: 69
  },
  {
    name: 'expiration_date',
    additional: 'ascending',
    orderField: 'expiration_date',
    orderType: 'asc',
    id: 70
  },
  {
    name: 'code_id',
    additional: 'descending',
    orderField: 'code_id',
    orderType: 'desc',
    id: 71
  },
  {
    name: 'code_id',
    additional: 'ascending',
    orderField: 'code_id',
    orderType: 'asc',
    id: 72
  },
  {
    name: 'payout',
    additional: 'descending',
    orderField: 'payout',
    orderType: 'desc',
    id: 73
  },
  {
    name: 'payout',
    additional: 'ascending',
    orderField: 'payout',
    orderType: 'asc',
    id: 74
  },
  {
    name: '500',
    orderField: '500',
    orderType: '500',
    icon: 'layout-grid',
    id: 75
  },
  {
    name: '1000',
    orderField: '1000',
    orderType: '1000',
    icon: 'layout-grid',
    id: 76
  },
  {
    name: 'registration_date',
    additional: 'descending',
    orderField: 'registration_date',
    orderType: 'desc',
    id: 77
  },
  {
    name: 'registration_date',
    additional: 'ascending',
    orderField: 'registration_date',
    orderType: 'asc',
    id: 78
  },
  {
    name: 'errors_quantity',
    additional: 'descending',
    orderField: 'quantity',
    orderType: 'desc',
    id: 79
  },
  {
    name: 'errors_quantity',
    additional: 'ascending',
    orderField: 'quantity',
    orderType: 'asc',
    id: 80
  },
  {
    name: 'created_at',
    additional: 'descending',
    orderField: 'id',
    orderType: 'desc',
    id: 81
  },
  {
    name: 'created_at',
    additional: 'ascending',
    orderField: 'id',
    orderType: 'asc',
    id: 82
  },
  {
    name: 'incent_campaign_id',
    additional: 'ascending',
    orderField: 'admin_program_id',
    orderType: 'asc',
    id: 83
  },
  {
    name: 'incent_campaign_id',
    additional: 'descending',
    orderField: 'admin_program_id',
    orderType: 'desc',
    id: 84
  },
  {
    name: 'smartlink_campaign_id',
    additional: 'ascending',
    orderField: 'admin_program_id',
    orderType: 'asc',
    id: 85
  },
  {
    name: 'smartlink_campaign_id',
    additional: 'descending',
    orderField: 'admin_program_id',
    orderType: 'desc',
    id: 86
  },
  {
    name: '12',
    orderField: '12',
    orderType: '12',
    icon: 'layout-grid',
    id: 87
  },
  {
    name: '24',
    orderField: '24',
    orderType: '24',
    icon: 'layout-grid',
    id: 88
  },
  {
    name: '48',
    orderField: '48',
    orderType: '48',
    icon: 'layout-grid',
    id: 89
  },
  {
    name: '96',
    orderField: '96',
    orderType: '96',
    icon: 'layout-grid',
    id: 90
  },
  {
    name: 'rating',
    additional: 'descending',
    orderField: 'rating',
    orderType: 'desc',
    // icon: 'star',
    id: 91
  },
  {
    name: 'rating',
    additional: 'ascending',
    orderField: 'rating',
    orderType: 'asc',
    // icon: 'star',
    id: 92
  }
] as const;

export const dropdownValues = ValuesForDropdown;

export const campaignModels = {
  1: 'CPL',
  2: 'CPS',
  3: 'CPA',
  4: 'PPI',
  5: 'COD'
};

// leads statuses frontend - backend pairs - key is the name of the status that is translated properly in frontend, value is the
// name of the status that is sent to backend
export const leadsBackendStatuses = {
  toPay: 'accepted',
  accepted: 'payment_waiting',
  pending: 'waiting',
  rejected: 'rejected'
};

// id of offerwall program, used to add postbacks
// export const offerwallProgramId = 421449;
export const offerwallProgramId = 4466;

export const urlParameters = [
  'transaction_id',
  'program_id',
  'program_name',
  'program_config_id',
  'payout',
  'payout_decimal',
  'status',
  'currency',
  'ip',
  'country_code',
  'ml_sub1',
  'ml_sub2',
  'ml_sub3',
  'ml_sub4',
  'ml_sub5',
  'player_id',
  'virtual_amount',
  'destination_program_id',
  'destination_program_name',
  'goal_name',
  'goal_id',
  'cart_value',
  'cart_value_original'
  // 'gaid',
  // 'ifda'
];

export const urlParametersMockedValues = {
  transaction_id: '123test123',
  program_id: '0',
  program_name: 'Test Program',
  program_config_id: '0',
  payout: '100',
  payout_decimal: '1.00',
  status: '-1',
  currency: 'EUR',
  ip: '127.0.0.1',
  country_code: 'PL',
  ml_sub1: 'test ml_sub1',
  ml_sub2: 'test ml_sub2',
  ml_sub3: 'test ml_sub3',
  ml_sub4: 'test ml_sub4',
  ml_sub5: 'test ml_sub5',
  player_id: '01bea103-80b7-479c-bccb-ff32d2dd3add',
  virtual_amount: '70',
  destination_program_id: '0',
  destination_program_name: 'Test destination Program',
  goal_name: 'test goal_name',
  goal_id: '0',
  cart_value: '0',
  cart_value_original: '0'
};
